import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Check, Building2, User } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const companyPlans = [
  {
    name: 'Free',
    price: '$0',
    description: 'For startups exploring options',
    features: [
      'Post briefs',
      'Browse CXOs',
      'Basic matching',
      '3 active briefs',
    ],
  },
  {
    name: 'Professional',
    price: '$299',
    period: '/mo',
    description: 'For growing companies',
    features: [
      'Unlimited briefs',
      'Priority matching',
      'Advanced filters',
      'Compare view',
      'Dedicated support',
    ],
    highlighted: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    description: 'For large organizations',
    features: [
      'SSO',
      'API access',
      'Custom contracts',
      'Dedicated account manager',
      'SLA guarantees',
    ],
  },
];

const cxoPlans = [
  {
    name: 'Free',
    price: '$0',
    description: 'For executives starting out',
    features: ['1 package', 'Standard matching', 'Manual invoicing'],
  },
  {
    name: 'Professional',
    price: '$99',
    period: '/mo',
    description: 'For active CXOs',
    features: [
      'Unlimited packages',
      'Verified badge',
      'Calendar sync',
      'Proposal templates',
    ],
    highlighted: true,
  },
  {
    name: 'Elite',
    price: '$199',
    period: '/mo',
    description: 'For top performers',
    features: [
      'Featured placement',
      'Instant lead notifications',
      'Automated invoicing',
      'Reduced platform fee (10%)',
    ],
  },
];

export default function PricingSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const companyRef = useRef<HTMLDivElement>(null);
  const cxoRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Title animation
      gsap.fromTo(
        titleRef.current,
        { y: 24, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          scrollTrigger: {
            trigger: titleRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Company section
      gsap.fromTo(
        companyRef.current,
        { x: '-4vw', opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.6,
          scrollTrigger: {
            trigger: companyRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // CXO section
      gsap.fromTo(
        cxoRef.current,
        { x: '4vw', opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.6,
          delay: 0.1,
          scrollTrigger: {
            trigger: cxoRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Cards stagger
      cardsRef.current.forEach((card, index) => {
        if (card) {
          gsap.fromTo(
            card,
            { y: 30, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.5,
              delay: 0.2 + index * 0.08,
              scrollTrigger: {
                trigger: card,
                start: 'top 90%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        }
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  let cardIndex = 0;

  return (
    <section
      ref={sectionRef}
      id="pricing"
      className="section-flowing bg-navy relative z-[120]"
    >
      <div className="w-full px-6 lg:px-12 max-w-7xl mx-auto">
        {/* Title */}
        <div ref={titleRef} className="text-center mb-16 opacity-0">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-teal mb-4 block">
            Pricing
          </span>
          <h2 className="font-heading text-h2 sm:text-h1 font-bold text-white max-w-3xl mx-auto">
            Transparent pricing for both sides
          </h2>
        </div>

        {/* Two Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Companies */}
          <div ref={companyRef} className="opacity-0">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-teal/10 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-teal" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-white">
                For Companies
              </h3>
            </div>

            <div className="space-y-4">
              {companyPlans.map((plan) => (
                <div
                  key={plan.name}
                  ref={(el) => { cardsRef.current[cardIndex++] = el; }}
                  className={`surface-panel p-6 opacity-0 ${
                    plan.highlighted ? 'border-teal/50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="font-heading text-lg font-semibold text-white mb-1">
                        {plan.name}
                      </h4>
                      <p className="text-sm text-slate">{plan.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-heading text-2xl font-bold text-teal">
                        {plan.price}
                      </p>
                      {plan.period && (
                        <span className="text-sm text-slate">{plan.period}</span>
                      )}
                    </div>
                  </div>

                  <ul className="space-y-2">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-teal" />
                        <span className="text-sm text-slate">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* CXOs */}
          <div ref={cxoRef} className="opacity-0">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-teal/10 flex items-center justify-center">
                <User className="w-5 h-5 text-teal" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-white">
                For CXOs
              </h3>
            </div>

            <div className="space-y-4">
              {cxoPlans.map((plan) => (
                <div
                  key={plan.name}
                  ref={(el) => { cardsRef.current[cardIndex++] = el; }}
                  className={`surface-panel p-6 opacity-0 ${
                    plan.highlighted ? 'border-teal/50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="font-heading text-lg font-semibold text-white mb-1">
                        {plan.name}
                      </h4>
                      <p className="text-sm text-slate">{plan.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-heading text-2xl font-bold text-teal">
                        {plan.price}
                      </p>
                      {plan.period && (
                        <span className="text-sm text-slate">{plan.period}</span>
                      )}
                    </div>
                  </div>

                  <ul className="space-y-2">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-teal" />
                        <span className="text-sm text-slate">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Platform Fee Note */}
        <div className="mt-12 text-center">
          <p className="text-sm text-slate">
            Platform take-rate: <span className="text-teal font-medium">15%</span>{' '}
            on all engagements (reduced to{' '}
            <span className="text-teal font-medium">10%</span> for Elite CXOs)
          </p>
        </div>
      </div>
    </section>
  );
}
